import { useState } from 'react';
import {
	ActionButton,
	BaseCard,
	EmptyState,
	ExportDropdown,
	FilterChip,
	ListPageLayout,
	Pagination,
	SectionCard,
	SideListCard,
	SkeletonBox,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { DefensivesTable } from '../components/Tables/DefensivesTable/DefensivesTable';
import { FertilizersTable } from '../components/Tables/FertilizersTable/FertilizersTable';
import { ManufacturersTable } from '../components/Tables/ManufacturersTable/ManufacturersTable';
import type { UseProductsPageProps } from '../hooks/useProductsPage';
import { useProductsPage } from '../hooks/useProductsPage';
import { useImportResultToast } from '../hooks/useImportResultToast';
import { useImportDefensiveFile } from '../../api/hooks/defensives/useImportDefensiveFile';
import { useImportFertilizerFile } from '../../api/hooks/fertilizers/useImportFertilizerFile';
import { useImportManufacturerFile } from '../../api/hooks/manufacturers/useImportManufacturerFile';
import { useGetMostPopularManufacturers } from '../../api/hooks/manufacturers/useGetMostPopularManufacturers';
import { useGetMostRecommendedProduct } from '../../api/hooks/data-reports/useGetMostRecommendedProduct';
import type { ProductsTab } from '../types';
import {
	exportDefensivesToExcel,
	exportDefensivesToPdf,
	exportFertilizersToExcel,
	exportFertilizersToPdf,
	exportManufacturersToExcel,
	exportManufacturersToPdf,
} from '../utils/exportProducts';
import { ImportProductsModal } from '../components/ImportProductsModal/ImportProductsModal';
import { ProductPhotoCarousel } from '../components/ProductPhotoCarousel/ProductPhotoCarousel';
import { ProductModel } from '../components/3D/ProductModel';
import styles from './ProductsPage.module.css';
import { FactoryOutlined } from '@mui/icons-material';
import { ProductsPageSkeleton } from '../components/Skeletons/ProductsPageSkeleton';

const ICON_BG_CLASSES = [styles.iconBgA, styles.iconBgB, styles.iconBgC];

const ICON_CLASSES = [styles.iconA, styles.iconB, styles.iconC];

export interface ProductsPageProps extends UseProductsPageProps {
	onAddNew?: (tab: ProductsTab) => void;
	onViewItem?: (tab: ProductsTab, id: string) => void;
	addButtonText?: string;
	onAddButtonClick?: () => void;
	onImport?: (file: File, tab: ProductsTab) => void;
}

export const ProductsPage = ({
	onAddNew,
	onViewItem,
	addButtonText,
	onAddButtonClick,
	onImport,
	...hookProps
}: ProductsPageProps) => {
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const importDefensiveFile = useImportDefensiveFile();
	const importFertilizerFile = useImportFertilizerFile();
	const importManufacturerFile = useImportManufacturerFile();
	const { showImportResultToast } = useImportResultToast();
	const { data: mostPopularProduct, isLoading: isMostPopularProductLoading } =
		useGetMostRecommendedProduct();
	const { data: mostPopularManufacturers = [], isLoading: isMostPopularManufacturersLoading } =
		useGetMostPopularManufacturers();
	const {
		activeTab,
		setActiveTab,
		filteredDefensives,
		filteredFertilizers,
		filteredManufacturers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		resolveUserName,
		isLoading,
	} = useProductsPage(hookProps);

	if (isLoading) return <ProductsPageSkeleton />;

	const tabs: SectionTab<ProductsTab>[] = [
		{
			key: 'defensives',
			label: 'Defensivos',
			content: (
				<DefensivesTable
					defensives={filteredDefensives}
					resolveUserName={resolveUserName}
					onRowClick={onViewItem ? (id) => onViewItem('defensives', id) : undefined}
				/>
			),
		},
		{
			key: 'fertilizers',
			label: 'Fertilizantes',
			content: (
				<FertilizersTable
					fertilizers={filteredFertilizers}
					resolveUserName={resolveUserName}
					onRowClick={onViewItem ? (id) => onViewItem('fertilizers', id) : undefined}
				/>
			),
		},
		{
			key: 'manufacturers',
			label: 'Fabricantes',
			content: (
				<ManufacturersTable
					manufacturers={filteredManufacturers}
					onRowClick={onViewItem ? (id) => onViewItem('manufacturers', id) : undefined}
				/>
			),
		},
	];

	const sideListItems = mostPopularManufacturers.map((manufacturer, index) => ({
		id: manufacturer._id,
		label: manufacturer.name,
		sublabel: `${manufacturer.totalProducts} ${manufacturer.totalProducts === 1 ? 'produto atrelado' : 'produtos atrelados'}`,
		icon: (
			<div className={`${styles.iconWrapper} ${ICON_BG_CLASSES[index % ICON_BG_CLASSES.length]}`}>
				<FactoryOutlined
					className={`${styles.factoryIcon} ${ICON_CLASSES[index % ICON_CLASSES.length]}`}
				/>
			</div>
		),
	}));

	const popularProductSlot = isMostPopularProductLoading ? (
		<SkeletonBox height="11rem" borderRadius="0.75rem" />
	) : !mostPopularProduct ? (
		<EmptyState
			title="Nenhum produto em destaque ainda"
			description="Quando um produto for recomendado em alguma análise, ele aparece aqui automaticamente."
		/>
	) : mostPopularProduct.avatars.length ? (
		<ProductPhotoCarousel images={mostPopularProduct.avatars} />
	) : (
		<ProductModel
			type={mostPopularProduct.type === 'defensive' ? 'defensives' : 'fertilizers'}
			nature={mostPopularProduct.nature ?? ''}
			name={mostPopularProduct.name}
			manufacturer={mostPopularProduct.manufacturer}
		/>
	);

	const handleAddClick = onAddButtonClick ?? (() => onAddNew?.(activeTab));

	const handleImportFile = (file: File) => {
		if (onImport) {
			onImport(file, activeTab);
			return;
		}

		switch (activeTab) {
			case 'defensives':
				importDefensiveFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
			case 'fertilizers':
				importFertilizerFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
			case 'manufacturers':
				importManufacturerFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
		}
	};

	return (
		<>
			<ImportProductsModal
				isOpen={isImportModalOpen}
				onClose={() => setIsImportModalOpen(false)}
				onImport={handleImportFile}
			/>
			<ListPageLayout
				sidebarWidth="26rem"
				sidebarW1500="22rem"
				sidebarW1300="19rem"
				sidebarW1200="17rem"
				stackAt={1100}
				sidebar={
					<div className={styles.charts}>
						<BaseCard title="Produto Mais Popular" subtitle="O produto mais usados no Practium">
							{popularProductSlot}
						</BaseCard>
						<SideListCard
							title="Fabricantes Populares"
							subtitle="Os fabricantes mais usados no Practium"
							items={sideListItems}
							isLoading={isMostPopularManufacturersLoading}
							stretch={true}
						/>
					</div>
				}
			>
				<SectionCard
					title="Catálogo de Produtos"
					subtitle="Gerencie defensivos, fertilizantes e fabricantes cadastrados no sistema"
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					sortAsc={sortAsc}
					onToggleSort={toggleSort}
					filterSlot={
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
						/>
					}
					footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
					headerAction={
						<>
							{addButtonText && <ActionButton text={addButtonText} onClick={handleAddClick} />}
							{hookProps.permissions.canCreate && (
								<ActionButton text="Importar" onClick={() => setIsImportModalOpen(true)} />
							)}
							<ExportDropdown
								onExportExcel={() => {
									if (activeTab === 'defensives')
										exportDefensivesToExcel(filteredDefensives, resolveUserName);
									else if (activeTab === 'fertilizers')
										exportFertilizersToExcel(filteredFertilizers, resolveUserName);
									else exportManufacturersToExcel(filteredManufacturers);
								}}
								onExportPdf={() => {
									if (activeTab === 'defensives')
										exportDefensivesToPdf(filteredDefensives, resolveUserName);
									else if (activeTab === 'fertilizers')
										exportFertilizersToPdf(filteredFertilizers, resolveUserName);
									else exportManufacturersToPdf(filteredManufacturers);
								}}
							/>
						</>
					}
				/>
			</ListPageLayout>
		</>
	);
};
